import { useCallback, useRef, useState } from "react";
import { GeoErrorReason, GeoState } from "../declarations";

export function useBrowserLocation() {
  const [state, setState] = useState<GeoState>({ status: "idle" });

  // Prevent setState after unmount
  const isMountedRef = useRef(true);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setState({ status: "error", reason: "unsupported" });
      return;
    }

    setState({ status: "loading" });

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!isMountedRef.current) {
          return;
        }

        setState({
          status: "success",
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracyMeters: pos.coords.accuracy,
        });
      },
      (err) => {
        if (!isMountedRef.current) {
          return;
        }

        let reason: GeoErrorReason = "unknown";

        switch (err.code) {
          case err.PERMISSION_DENIED:
            reason = "denied";
            break;
          case err.TIMEOUT:
            reason = "timeout";
            break;
          case err.POSITION_UNAVAILABLE:
            reason = "unavailable";
            break;
        }

        setState({ status: "error", reason });
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 60_000,
      }
    );
  }, []);

  // Cleanup on unmount
  const dispose = useCallback(() => {
    isMountedRef.current = false;
  }, []);

  return {
    state,
    requestLocation,
    dispose,
  };
}
